/**
 * Hooks for endpoint relationship management
 */

import { useState, useEffect, useCallback } from 'react'
import type {
  EndpointRelationship,
  CreateRelationshipRequest,
  UpdateRelationshipRequest,
  MarkRelationshipOpenQuestionRequest,
  ResolveRelationshipOpenQuestionRequest,
} from '../types/case-container'
import {
  listRelationships,
  listOpenQuestions,
  countOpenQuestions,
  createRelationship,
  updateRelationship,
  deleteRelationship,
  markAsOpenQuestion,
  resolveOpenQuestion,
} from '../services/endpoint-relationships'
import { runMutation } from './use-collection-requests/run-mutation'

// =============================================================================
// useRelationships - List relationships for a case
// =============================================================================

interface UseRelationshipsResult {
  relationships: EndpointRelationship[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch relationships for a case
 */
export function useRelationships(caseId: string | null): UseRelationshipsResult {
  const [relationships, setRelationships] = useState<EndpointRelationship[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId) {
      setRelationships([])
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await listRelationships(caseId)
      setRelationships(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch relationships')
      setRelationships([])
    } finally {
      setLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { relationships, loading, error, refetch: fetch }
}

// =============================================================================
// useOpenQuestions - List open questions for a case
// =============================================================================

interface UseOpenQuestionsResult {
  openQuestions: EndpointRelationship[]
  count: number
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch open questions for a case
 */
export function useOpenQuestions(caseId: string | null): UseOpenQuestionsResult {
  const [openQuestions, setOpenQuestions] = useState<EndpointRelationship[]>([])
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId) {
      setOpenQuestions([])
      setCount(0)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const [questions, countResult] = await Promise.all([
        listOpenQuestions(caseId),
        countOpenQuestions(caseId),
      ])
      setOpenQuestions(questions)
      setCount(countResult.count)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch open questions')
      setOpenQuestions([])
      setCount(0)
    } finally {
      setLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { openQuestions, count, loading, error, refetch: fetch }
}

// =============================================================================
// useRelationshipMutations - Create, update, delete relationships
// =============================================================================

interface UseRelationshipMutationsResult {
  creating: boolean
  updating: boolean
  deleting: boolean
  error: string | null
  create: (caseId: string, input: CreateRelationshipRequest) => Promise<EndpointRelationship | null>
  update: (
    caseId: string,
    relationshipId: string,
    input: UpdateRelationshipRequest
  ) => Promise<EndpointRelationship | null>
  remove: (caseId: string, relationshipId: string) => Promise<boolean>
  markOpen: (
    caseId: string,
    relationshipId: string,
    request?: MarkRelationshipOpenQuestionRequest
  ) => Promise<EndpointRelationship | null>
  resolve: (
    caseId: string,
    relationshipId: string,
    request?: ResolveRelationshipOpenQuestionRequest
  ) => Promise<EndpointRelationship | null>
  clearError: () => void
}

/**
 * Hook for relationship mutations
 */
export function useRelationshipMutations(): UseRelationshipMutationsResult {
  const [creating, setCreating] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const create = useCallback(
    (caseId: string, input: CreateRelationshipRequest) => runMutation(
      { setLoading: setCreating, setError, errorMessage: 'Failed to create relationship', fallback: null },
      () => createRelationship(caseId, input)
    ), []
  )

  const update = useCallback(
    (caseId: string, relationshipId: string, input: UpdateRelationshipRequest) => runMutation(
      { setLoading: setUpdating, setError, errorMessage: 'Failed to update relationship', fallback: null },
      () => updateRelationship(caseId, relationshipId, input)
    ), []
  )

  const remove = useCallback(
    (caseId: string, relationshipId: string) => runMutation(
      { setLoading: setDeleting, setError, errorMessage: 'Failed to delete relationship', fallback: false },
      async () => { await deleteRelationship(caseId, relationshipId); return true }
    ), []
  )

  const markOpen = useCallback(
    (caseId: string, relationshipId: string, request?: MarkRelationshipOpenQuestionRequest) => runMutation(
      { setLoading: setUpdating, setError, errorMessage: 'Failed to mark as open question', fallback: null },
      () => markAsOpenQuestion(caseId, relationshipId, request)
    ), []
  )

  const resolve = useCallback(
    (caseId: string, relationshipId: string, request?: ResolveRelationshipOpenQuestionRequest) => runMutation(
      { setLoading: setUpdating, setError, errorMessage: 'Failed to resolve open question', fallback: null },
      () => resolveOpenQuestion(caseId, relationshipId, request)
    ), []
  )

  const clearError = useCallback(() => { setError(null) }, [])

  return {
    creating, updating, deleting, error,
    create, update, remove, markOpen, resolve, clearError,
  }
}
